import React, { MutableRefObject, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import Modal from '../Modal'
import { initialIframeSetting } from '../../app/config'
import { ExternalLinkIcon } from '../../icons/Icons'
import { openNewTab } from '../../helper/openNewTab'
import { keyType } from '../../constants/string'
import { sendUserEventToParent } from '../../actions/static'
import { IframeSettingsType } from '../../models/IframeSettingsType'
import { SettingsType } from '../../models/SettingsType'
import { userActionEvents } from '../../constants/userActionEvents'
import { focusTrapCreator } from '../../helper/focusTrapCreator'

import {
  PatientReviewButton,
  PatientReviewButtonsContainer,
  PatientReviewContent,
  PatientReviewPaper
} from './PatientReviewModal.style'

interface Props {
  settings: SettingsType
  setIframe: (p: IframeSettingsType) => void
  iframeSettings: IframeSettingsType
  persistentMenuButtonRef: MutableRefObject<HTMLButtonElement>
}

const PatientReviewModal: React.FC<Props> = ({
  settings,
  setIframe,
  iframeSettings,
  persistentMenuButtonRef
}) => {
  const {
    patientReview: {
      popupText,
      proceedButtonText,
      cancelButtonText,
      proceedButtonUrl
    }
  } = settings

  const output = useRef<HTMLDivElement>(null)
  const patientReviewModalRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    patientReviewModalRef.current.focus()
  }, [patientReviewModalRef.current])

  useEffect(() => {
    sendUserEventToParent(userActionEvents.EF_REVIEW)
    linkEditorListener()
  }, [])

  const linkEditorListener = (): void => {
    const node: NodeListOf<HTMLElement> = output.current.querySelectorAll('.linkEdited')
    const links = [...node]
    links.length > 0 &&
    links.forEach(link => {
      const extension = JSON.parse(link.getAttribute('data-extension'))
      const url = link.getAttribute('data-href')
      const event = link.getAttribute('activity-event')
      if (!extension) link.innerHTML = `${link.innerHTML}${ExternalLinkIcon(t('external_link_icon'))}`
      link.onclick = (e) => {
        e.preventDefault()
        sendUserEventToParent(event)
        if (extension) {
          handleCloseModal()
          setIframe({
            iframeSrc: url,
            isIframeOpen: true,
            questionnaireName: iframeSettings.questionnaireName
          })
        } else {
          openNewTab(url)
        }
      }
      link.onkeydown = (e) => {
        const currentKey = e.key.toLowerCase()
        if (currentKey === keyType.ENTER) {
          e.preventDefault()
          sendUserEventToParent(event)
          if (extension) {
            handleCloseModal()
            setIframe({
              iframeSrc: url,
              isIframeOpen: true,
              questionnaireName: iframeSettings.questionnaireName
            })
          } else {
            openNewTab(url)
          }
        }
      }
    })
  }

  const handleCloseModal = () => {
    sendUserEventToParent(userActionEvents.EF_REVIEW_CANCEL)
    closeModal()
  }

  const closeModal = () => {
    const copyIframeSettings = {
      ...initialIframeSetting,
      questionnaireName: iframeSettings.questionnaireName
    }

    setIframe(copyIframeSettings)
    persistentMenuButtonRef.current.focus()
  }

  const handleProceed = () => {
    sendUserEventToParent(userActionEvents.EF_REVIEW_PROCEED)
    openNewTab(proceedButtonUrl)
    closeModal()
  }

  return (
    <Modal
      onClose={handleCloseModal}
    >
      <PatientReviewPaper
        ref={patientReviewModalRef}
        tabIndex={0}
        role='dialog'
        aria-modal='true'
        aria-labelledby='patient-review-modal-title'
        onKeyDown={(e) => focusTrapCreator(e, patientReviewModalRef.current, true)}
      >
        <PatientReviewContent
          ref={output}
          id={'patient-review-modal-title'}
          dangerouslySetInnerHTML={{
            __html: popupText
          }}
        />
        <PatientReviewButtonsContainer>
          <PatientReviewButton
            agreeButton={false}
            onClick={handleCloseModal}
          >
            {cancelButtonText}
          </PatientReviewButton>
          <PatientReviewButton
            agreeButton={true}
            aria-label={t('link_to_external_page')}
            onClick={handleProceed}
          >
            {proceedButtonText}
          </PatientReviewButton>
        </PatientReviewButtonsContainer>
      </PatientReviewPaper>
    </Modal>
  )
}

export default PatientReviewModal